/*
 * Mobile nav — the ≤860px navigation's shared state.
 *
 * TWO ELEMENTS REGISTER THIS FACTORY, not one: the burger in the header
 * (components/interactive/mobile-nav) and the sheet in the body
 * (partials/_mobile-sheet). They cannot be one element, because `.site-header` has a
 * backdrop-filter and would become the containing block for the sheet's `position: fixed`.
 * $store.nav.mobileOpen is what joins them — that is what the store is for, and it is
 * also why it survives Swup navigations.
 */

/** The design's breakpoint. Stated in _atoms.css too; both must move together. */
const DESKTOP = '(min-width: 861px)';

export default function mobileNav() {
    return {
        get isOpen() {
            return this.$store.nav.mobileOpen;
        },

        get isClosed() {
            return !this.$store.nav.mobileOpen;
        },

        get expandedAttr() {
            return String(this.$store.nav.mobileOpen);
        },

        open() {
            this.$store.nav.mobileOpen = true;
        },

        close() {
            const wasOpen = this.$store.nav.mobileOpen;
            this.$store.nav.mobileOpen = false;

            if (wasOpen) {
                this.returnFocusToTrigger();
            }
        },

        toggle() {
            if (this.$store.nav.mobileOpen) {
                this.close();
            } else {
                this.open();
            }
        },

        /*
         * PUT FOCUS BACK ON THE BURGER WITHOUT SCROLLING THE PAGE.
         *
         * x-trap carries `.noreturn`, so focus-trap does NOT restore focus itself, and
         * this is the replacement rather than an addition. Its own restore called
         * `element.focus()` with no options, and the browser scrolled that element into
         * view: from 900px down the home page, closing the sheet threw the reader back to
         * scroll 0 — the page visibly jumping upward, which is the bug this replaces.
         * `preventScroll: true` is the whole difference; focus-trap does not expose the
         * option through the directive, which is why the restore has to happen here.
         *
         * DOING NOTHING WAS NOT AN OPTION: `.noreturn` alone leaves focus on <body> after
         * close, so a keyboard user who opened the menu loses their place in the document
         * entirely. Returning them to the control they activated is the correct behaviour.
         *
         * Deferred a frame because x-trap deactivates in its own effect, and focusing
         * while the trap is still tearing down gets the focus pulled back into the sheet.
         */
        returnFocusToTrigger() {
            /*
             * RETRIED ACROSS A FEW FRAMES, NOT FOCUSED ONCE, because the trigger may still
             * be INERT when we get there. `x-trap.inert` marks the sheet's siblings inert
             * while it is open, and the header — which holds the burger — is one of them.
             * Alpine clears that in its own effect when the store flips; a single
             * requestAnimationFrame can easily land first, and `.focus()` on an element
             * inside an inert subtree does nothing at all, silently. Focus then stays on
             * <body> and the trigger is lost.
             *
             * Retrying until `document.activeElement` actually IS the trigger avoids
             * guessing at that ordering. Bounded, so a genuinely unfocusable trigger (the
             * viewport crossed to desktop and the burger is display:none) costs 5 frames
             * and stops, rather than spinning.
             */
            let attempts = 5;

            const attempt = () => {
                const trigger = document.querySelector('[data-mobile-nav-trigger]');

                if (!trigger || trigger.offsetParent === null) {
                    return;
                }

                trigger.focus({ preventScroll: true });

                if (document.activeElement !== trigger && --attempts > 0) {
                    requestAnimationFrame(attempt);
                }
            };

            requestAnimationFrame(attempt);
        },

        /*
         * CLOSE WHEN THE VIEWPORT CROSSES INTO DESKTOP. Hiding `.msheet` in CSS at this
         * breakpoint is not enough on its own: the store would still say open, so
         * `x-trap.noscroll` would hold the body scroll lock and the page would be
         * unscrollable at desktop width with nothing on screen to explain it. Closing the
         * store is what releases the trap.
         *
         * Both instances attach a listener and both set the same value to false, which is
         * idempotent — not worth a coordination flag.
         */
        init() {
            this.desktop = window.matchMedia(DESKTOP);
            this.onDesktop = (e) => {
                if (e.matches) {
                    this.close();
                }
            };
            this.desktop.addEventListener('change', this.onDesktop);
        },

        destroy() {
            if (this.desktop) {
                this.desktop.removeEventListener('change', this.onDesktop);
            }
        },
    };
}
